import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import type { ComponentType } from "react";
import type { BlockType } from "./BlockTypeMenu";
import { useBlockTypes } from "./useBlockTypes";
import type { InsertionDto } from "@/hooks/useInsertions";
import { IconWand, IconBraces, IconLetterCase, IconFileImport, IconCamera } from "@tabler/icons-react";
import { askMathFromPhoto } from "@/lib/askMath";
import type { Icon } from "@tabler/icons-react";
import styles from "./SlashCommandMenu.module.scss";
import { slashCommandMatches } from "./slashModel";
import { useTranslation } from "@/utils/hooks/useTranslation";
import { useToast } from "@/components/shared/Toast";
import { useEditorStore } from "@/store/editorStore";
import {
  effectiveDocSettings,
  generate,
  isLoremSlashPrefix,
  LOREM_FIRED_EVENT,
  type LoremFiredEventDetail,
  parseLoremSlash,
  resolveLoremSlash,
} from "@/lib/lorem";
import {
  TOOLBOX_INSERT_BATCH_EVENT,
  type ToolboxInsertBatchEventDetail,
} from "@/components/toolbox/insertHandler";

/**
 * Action-style entry that the slash menu can fire alongside block
 * insertions and LaTeX tokens (LILIA-142). Used today by the
 * `/columns` entry that wraps the current block in a layout group;
 * future commands (e.g. `/highlight`, `/source-from`) reuse the same
 * shape.
 */
export interface SlashCommand {
  /** Stable id used as a React key. */
  id: string;
  /** Visible title (e.g. "2-column region"). */
  label: string;
  /** Helper text below the label. */
  description: string;
  /** Match terms — the command shows when any term contains the query. */
  keywords: string[];
  /** Tabler icon component. */
  icon: Icon | ComponentType<{ size?: number }>;
  /**
   * Fired when the user picks the command. The consumer wires the
   * actual side-effect; the menu just closes after invocation.
   * `query` is the live slash-menu input so commands like `/lorem 5`
   * can read the count from it.
   */
  run: (query: string) => void;
  /**
   * The command's own reading of the query, for commands that take an
   * argument (`/lorem 3`): the filter shows the command whenever this says
   * yes, even though "lorem 3" is in none of its keywords.
   */
  matches?: (query: string) => boolean;
}

/** Detail of the `openImportLatex` event when the slash menu sends it. */
export interface OpenImportLatexDetail {
  /** Opened from the slash menu: the import lands where the `/` was. */
  atSlash?: boolean;
}

interface SlashCommandMenuProps {
  anchor: HTMLElement | null;
  show: boolean;
  query: string;
  onSelect: (type: BlockType) => void;
  onClose: () => void;
  caretCoords?: { top: number; left: number; bottom: number } | null;
  /**
   * Available LaTeX insertions for this document. Surfaced as a second
   * section in the menu (only when query is non-empty so the default
   * `/` open stays focused on block types). Same data source as the
   * InsertionsPanel side panel and the ⌘K palette — useInsertions(docId).
   */
  insertions?: InsertionDto[];
  /** Fires when the user picks a LaTeX insertion. Inserts at cursor; doesn't convert the block. */
  onInsertToken?: (token: InsertionDto) => void;
  /** Action-style commands surfaced as a third menu section (LILIA-142).
   *  Filtered by query against the `keywords` field. */
  commands?: SlashCommand[];
  /**
   * Removes what was typed to reach the menu — the `/` and the filter text —
   * before any row runs (Olivia, 27 Sep 2026: every command consumes it).
   * The host owns the document, so it does the removing; the menu calls it
   * from the one place every row goes through.
   */
  onConsume?: () => void;
}

// Internal item shape — unifies block-type rows and insertion rows so
// keyboard navigation and selection treat them uniformly.
type MenuItem =
  | { kind: "block"; index: number; block: ReturnType<typeof useBlockTypes>[number] }
  | { kind: "insert"; index: number; token: InsertionDto }
  | { kind: "command"; index: number; command: SlashCommand };

export function SlashCommandMenu({
  anchor, show, query, onSelect, onClose, caretCoords,
  insertions = [], onInsertToken, commands = [], onConsume,
}: SlashCommandMenuProps) {
  const { t } = useTranslation('editor');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const blockTypes = useBlockTypes();

  // Block-type matches — original behavior preserved.
  const blockMatches = blockTypes.filter((b) => {
    const q = query.toLowerCase();
    return (
      b.label.toLowerCase().includes(q) ||
      b.type.toLowerCase().includes(q) ||
      b.description.toLowerCase().includes(q)
    );
  });

  // Insertion matches — only when query is non-empty. Avoids dumping
  // ~60 rows on a bare `/`, and keeps the menu fast for the conversion
  // case which is the high-frequency one.
  const insertionMatches = useMemo(() => {
    if (!query.trim() || !onInsertToken) return [] as InsertionDto[];
    const q = query.toLowerCase();
    return insertions
      .filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          (i.notes ?? "").toLowerCase().includes(q) ||
          (i.semanticCategory ?? "").toLowerCase().includes(q)
      )
      .slice(0, 12); // hard cap so the menu stays scannable
  }, [insertions, query, onInsertToken]);

  // Action commands (LILIA-142). Filter against the keyword array so a
  // query like "col" surfaces "/2-column region" and "/3-column region"
  // without needing exact prefix match. Empty query shows all commands
  // — they're a small set, scannable.
  // Built-in slash commands — surfaced in every slash menu instance,
  // so a paste-LaTeX path is reachable from where the user actually
  // looks (alongside block-type insertions). Anyone listening for the
  // window event opens the right LaTeX-import modal (EditorPage for
  // Flow, BlockEditor for Studio).
  const BUILTIN: SlashCommand[] = useMemo(() => ([
    {
      id: "paste-latex",
      label: "Paste LaTeX…",
      description: "Convert a LaTeX snippet into native blocks (heading, theorem, equation…).",
      keywords: ["latex", "paste", "import", "theorem", "equation"],
      icon: IconFileImport,
      run: () => {
        const detail: OpenImportLatexDetail = { atSlash: true };
        window.dispatchEvent(new CustomEvent("openImportLatex", { detail }));
      },
    },
    {
      // Maths from a photo (18 Oct): Ask Lilia opens its picker and reads it.
      id: "maths-from-photo",
      label: t("askMath.slashLabel"),
      description: t("askMath.slashDescription"),
      keywords: ["photo", "picture", "image", "camera", "handwriting", "scan", "equation", "formula", "math", "maths", "photo", "équation", "formule", "ecuación", "fórmula"],
      icon: IconCamera,
      run: () => askMathFromPhoto(),
    },
  ]), [t]);

  const commandMatches = useMemo(() => {
    return [...BUILTIN, ...commands].filter((c) => slashCommandMatches(c, query));
  }, [BUILTIN, commands, query]);

  // Flattened items + a parallel meta array so keyboard nav can index
  // across all sections cleanly.
  const items: MenuItem[] = useMemo(() => {
    const out: MenuItem[] = [];
    blockMatches.forEach((b, i) => out.push({ kind: "block", index: i, block: b }));
    commandMatches.forEach((c, i) => out.push({ kind: "command", index: i, command: c }));
    insertionMatches.forEach((i, idx) => out.push({ kind: "insert", index: idx, token: i }));
    return out;
  }, [blockMatches, commandMatches, insertionMatches]);

  // Reset selected index when filter changes.
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelectorAll<HTMLElement>("[data-cmd-row]")[selectedIndex];
    active?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  // Every row runs through here, so every row consumes the typed text the
  // same way. The query is read before it goes: `/lorem 5` hands the command
  // its count, then none of it stays in the document.
  const fire = useCallback(
    (item: MenuItem) => {
      const typed = query;
      onConsume?.();
      if (item.kind === "block") {
        onSelect(item.block.type);
      } else if (item.kind === "command") {
        item.command.run(typed);
        onClose();
      } else if (onInsertToken) {
        onInsertToken(item.token);
      }
    },
    [onSelect, onInsertToken, onClose, onConsume, query]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!show) return;
      // Nothing matches: the keys belong to the text. Enter starts a new
      // line rather than vanishing into an empty menu.
      if (items.length === 0 && e.key !== "Escape") return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => (items.length === 0 ? 0 : (prev + 1) % items.length));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => (items.length === 0 ? 0 : (prev - 1 + items.length) % items.length));
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        const target = items[selectedIndex];
        if (target) fire(target);
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    },
    [show, items, selectedIndex, fire, onClose]
  );

  useEffect(() => {
    if (!show) return;
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [show, handleKeyDown]);

  // Close when clicking outside.
  useEffect(() => {
    if (!show) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        anchor &&
        !anchor.contains(target)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [show, anchor, onClose]);

  if (!show || !anchor) return null;

  // Use caret coordinates from ProseMirror if available, otherwise
  // fall back to browser selection or anchor.
  const rawPos = caretCoords
    ? { top: caretCoords.top, bottom: caretCoords.bottom, left: caretCoords.left }
    : (() => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
          const range = sel.getRangeAt(0);
          const rects = range.getClientRects();
          if (rects.length > 0) {
            return { top: rects[0].top, bottom: rects[0].bottom, left: rects[0].left };
          }
        }
        const r = anchor.getBoundingClientRect();
        return { top: r.top, bottom: r.bottom, left: r.left };
      })();

  // Menu height estimate (header ~40 + per-row ~44 + section gap ~28
  // when both sections are visible, capped at 370).
  const sectionGap = insertionMatches.length > 0 ? 28 : 0;
  const menuHeight = Math.min(items.length * 44 + 50 + sectionGap, 370);
  const viewportHeight = window.innerHeight;

  // Flip above cursor if menu would overflow the viewport bottom.
  const spaceBelow = viewportHeight - rawPos.bottom - 4;
  const fitsBelow = spaceBelow >= menuHeight;
  const top = fitsBelow
    ? rawPos.bottom + 4
    : Math.max(4, rawPos.top - menuHeight - 4);

  // Clamp left so menu doesn't overflow the right edge.
  const menuWidth = 320;
  const left = Math.min(rawPos.left, window.innerWidth - menuWidth - 8);

  return createPortal(
    <div
      className={styles.popup}
      style={{
        position: "fixed",
        top,
        left,
        zIndex: 1000,
      }}
    >
      <div ref={menuRef} className={styles.menu}>
        <div className={styles.header}>
          {t('insertBlock')}
          {query && <span className={styles.query}>/{query}</span>}
        </div>
        <div ref={listRef} className={styles.list}>
          {items.length === 0 ? (
            <div className={styles.empty}>{t('noMatchingBlocks')}</div>
          ) : (
            <>
              {blockMatches.length > 0 && blockMatches.map((block, i) => {
                const flatIdx = i; // block items occupy [0..blockMatches.length)
                const isSelected = flatIdx === selectedIndex;
                return (
                  <button
                    key={`block-${block.type}`}
                    data-cmd-row
                    data-testid={`slash-cmd-block-${block.type}`}
                    data-block-type={block.type}
                    className={`${styles.item} ${isSelected ? styles.selected : ""}`}
                    onClick={() => fire({ kind: "block", index: i, block })}
                    onMouseEnter={() => setSelectedIndex(flatIdx)}
                  >
                    <span className={styles.itemIcon}>
                      <block.icon size={18} />
                    </span>
                    <div className={styles.itemInfo}>
                      <strong>{block.label}</strong>
                      <span>{block.description}</span>
                    </div>
                    {isSelected && <span className={styles.kbdHint} aria-hidden>↩</span>}
                  </button>
                );
              })}

              {commandMatches.length > 0 && (
                <>
                  <div className={styles.sectionHeader}>Actions</div>
                  {commandMatches.map((command, i) => {
                    const flatIdx = blockMatches.length + i;
                    const isSelected = flatIdx === selectedIndex;
                    const Ico = command.icon;
                    return (
                      <button
                        key={`cmd-${command.id}`}
                        data-cmd-row
                        data-testid={`slash-cmd-${command.id}`}
                        className={`${styles.item} ${isSelected ? styles.selected : ""}`}
                        onClick={() => fire({ kind: "command", index: i, command })}
                        onMouseEnter={() => setSelectedIndex(flatIdx)}
                      >
                        <span className={styles.itemIcon}>
                          <Ico size={18} />
                        </span>
                        <div className={styles.itemInfo}>
                          <strong>{command.label}</strong>
                          <span>{command.description}</span>
                        </div>
                        {isSelected && <span className={styles.kbdHint} aria-hidden>↩</span>}
                      </button>
                    );
                  })}
                </>
              )}

              {insertionMatches.length > 0 && (
                <>
                  <div className={styles.sectionHeader}>LaTeX commands</div>
                  {insertionMatches.map((token, i) => {
                    const flatIdx = blockMatches.length + commandMatches.length + i;
                    const isSelected = flatIdx === selectedIndex;
                    const isEnv = token.kind === "environment" || token.expectsBody;
                    return (
                      <button
                        key={`insert-${token.packageSlug ?? "kernel"}-${token.kind}-${token.name}`}
                        data-cmd-row
                        className={`${styles.item} ${isSelected ? styles.selected : ""}`}
                        onClick={() => fire({ kind: "insert", index: i, token })}
                        onMouseEnter={() => setSelectedIndex(flatIdx)}
                      >
                        <span className={styles.itemIcon}>
                          {isEnv ? <IconBraces size={18} /> : <IconWand size={18} />}
                        </span>
                        <div className={styles.itemInfo}>
                          <strong>{isEnv ? `\\begin{${token.name}}…` : `\\${token.name}`}</strong>
                          <span>{token.notes ?? (token.packageSlug ?? "kernel")}</span>
                        </div>
                        {isSelected && <span className={styles.kbdHint} aria-hidden>↩</span>}
                      </button>
                    );
                  })}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
